// src/services/dealerApi.ts

import type { BaseParams } from '../types/api';
import type {
  Dealer,
  DealerPagination,
  DealerStatus,
  DealerStatusHistoryEntry,
  DealerSummary,
  DealersResponse,
  PointAdjustmentRequest,
  PointAdjustmentResponse,
  StatusSummary,
} from '../types/dealer';
import { api } from './api';
import { apiGet, apiPatch, apiPost } from './apiUtils';

export interface DealerParams extends BaseParams {
  subscription?: string;
  dealer_complex?: string;
  experience_years?: number;
  status?: string;
  search?: string;
}

export interface DealerListResponse {
  success: boolean;
  data: {
    list: Dealer[];
    summary: {
      status_summary: StatusSummary;
    };
    pagination: DealerPagination;
  };
  message: string;
}

export interface DealerDetailResponse {
  success: boolean;
  data: Dealer;
  message: string;
}

/** 딜러 상세에서 보여주는 이미지. license-scan 은 딜러가 찍은 종사원증 사진(비공개) */
export type DealerImageType = 'profile' | 'enhanced' | 'license-scan' | 'band-card';

/** 지금 번호의 종사원증 사진 판정 기록 */
export interface LicenseScanInfo {
  verdict: 'match' | 'check' | 'mismatch' | 'unchecked' | null;
  submitted_number: string | null;
  verified_number: string | null;
  attempts: number | null;
  width: number;
  height: number;
  captured_at: string | null;
  kept_at: string | null;
}

/** 관리자 확인 대기 요청 (끝까지 인식되지 않은 번호) */
export interface LicensePendingInfo {
  id: number;
  /** signup: 가입 때 직접 입력해 가입함 / replace: 내 정보 교체 요청 (번호는 아직 old_number) */
  source: 'signup' | 'replace';
  old_number: string | null;
  new_number: string;
  verdict: LicenseScanInfo['verdict'];
  requested_at: string | null;
  has_photo: boolean;
}

/** 딜러의 종사원증 — 지금 번호, 딜러 앱과 같은 상태, 사진 판정, 대기 요청 */
export interface DealerLicenseStatus {
  license_number: string | null;
  license_status: 'verified' | 'pending' | 'none';
  scan: LicenseScanInfo | null;
  pending: LicensePendingInfo | null;
}

const dealerImagePath = (dealerId: number, type: DealerImageType): string =>
  type === 'license-scan' ? `/dealer/${dealerId}/license-scan/image` : `/dealer/${dealerId}/image/${type}`;

export const dealerApi = {
  getDealers: (params: DealerParams = {}): Promise<DealersResponse> => {
    return apiGet<DealersResponse>('/dealer', {
      page: 1,
      per_page: 10,
      ...params,
    });
  },

  getDealer: (id: number): Promise<DealerDetailResponse> => {
    return apiGet<DealerDetailResponse>(`/dealer/${id}`);
  },

  /** 이용정지(suspended, 메모 필수) / 이용정지 해제(active) */
  updateDealerStatus: (
    id: number,
    status: 'active' | 'suspended',
    memo?: string,
  ): Promise<{ success: boolean; data: { status: DealerStatus; previous_status: DealerStatus }; message: string }> => {
    return apiPatch(`/dealer/${id}/status`, { status, memo: memo || null });
  },

  /** 신고 관리 딜러 요약 팝업 — reportId 는 지금 보는 신고 (그 신고의 인증링크를 같이 준다) */
  getDealerSummary: (id: number, reportId?: number): Promise<{ success: boolean; data: DealerSummary }> => {
    return apiGet(`/dealer/${id}/summary`, reportId ? { report_id: reportId } : {});
  },

  /** 상태 변경 기록 (최근 순) */
  getStatusHistory: (id: number): Promise<{ success: boolean; data: DealerStatusHistoryEntry[] }> => {
    return apiGet(`/dealer/${id}/status-history`);
  },

  approveDealer: (id: number): Promise<DealerDetailResponse> => {
    return apiPost<DealerDetailResponse>(`/dealer/${id}/approve`);
  },

  rejectDealer: (id: number, reason: string): Promise<DealerDetailResponse> => {
    return apiPost<DealerDetailResponse, { reason: string }>(`/dealer/${id}/reject`, { reason });
  },

  adjustPoints: (dealerId: number, data: PointAdjustmentRequest): Promise<PointAdjustmentResponse> => {
    return apiPost<PointAdjustmentResponse, PointAdjustmentRequest>(`/dealer/${dealerId}/points/adjust`, data);
  },

  /** 종사원증 상태·사진 판정·대기 요청 */
  getLicenseScan: (dealerId: number): Promise<{ success: boolean; data: DealerLicenseStatus }> => {
    return apiGet(`/dealer/${dealerId}/license-scan`);
  },

  /** 확인 대기 요청을 '확인됨'으로 (교체 요청이면 요청 번호로 바뀐다) */
  approveLicenseChange: (changeId: number): Promise<{ success: boolean; message: string }> => {
    return apiPost(`/license-changes/${changeId}/approve`);
  },

  /** 대기 요청의 근거 사진을 blob URL 로. 없으면 null. 쓰고 나면 revokeObjectURL */
  fetchLicenseChangeImageUrl: async (changeId: number): Promise<string | null> => {
    try {
      const response = await api.get(`/license-changes/${changeId}/image`, { responseType: 'blob' });
      return window.URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  },

  /**
   * 딜러 이미지를 받아 미리보기용 blob URL 로 돌려준다. 없으면 null.
   * 서버는 토큰을 Authorization 헤더로만 받으므로 <img src> 에 API 주소를 바로 넣을 수 없다.
   * 쓰고 나면 URL.revokeObjectURL 로 정리해야 한다.
   */
  fetchImageObjectUrl: async (dealerId: number, type: DealerImageType): Promise<string | null> => {
    try {
      const response = await api.get(dealerImagePath(dealerId, type), { responseType: 'blob' });
      return window.URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  },

  /**
   * 딜러 이미지 다운로드
   * imageUrl이 제공되면 해당 URL에서 직접 다운로드, 없으면 API 엔드포인트 사용
   */
  downloadImage: async (
    dealerId: number,
    type: DealerImageType,
    dealerName?: string,
    imageUrl?: string,
  ): Promise<void> => {
    const typeLabels: Record<string, string> = {
      profile: '원본사진',
      enhanced: '보정사진',
      'license-scan': '종사원증사진',
      'band-card': '밴드카드',
    };
    try {
      let response;
      if (imageUrl) {
        // 직접 URL에서 다운로드 (S3 등 외부 URL)
        response = await fetch(imageUrl);
        if (!response.ok) {
          if (response.status === 404) {
            const { toast } = await import('sonner');
            toast.error('해당 이미지가 등록되지 않았습니다.');
            return;
          }
          throw new Error(`Download failed: ${response.status}`);
        }
        const blob = await response.blob();
        const contentType = response.headers.get('content-type') || 'image/png';
        const ext = contentType.includes('jpeg') || contentType.includes('jpg') ? 'jpg' : 'png';
        const filename = dealerName ? `${dealerName}_${typeLabels[type] || type}.${ext}` : `${type}.${ext}`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }

      response = await api.get(dealerImagePath(dealerId, type), {
        params: { download: true },
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'] || 'image/png';
      const ext = contentType.includes('jpeg') ? 'jpg' : 'png';
      let filename = dealerName ? `${dealerName}_${typeLabels[type] || type}.${ext}` : `${type}.${ext}`;

      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition && !dealerName) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)"?/);
        if (match?.[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          const { toast } = await import('sonner');
          toast.error('해당 이미지가 등록되지 않았습니다.');
          return;
        }
      }
      const { toast } = await import('sonner');
      toast.error('이미지 다운로드에 실패했습니다.');
    }
  },
};
